import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import moment from "moment";
import "moment/locale/es";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import cuideoTheme from "../../../../containers/themes/defaultTheme";
import { useClientDiscardedValuations } from "../../features/valuation";

const StyledTable = styled(Table)`
  .MuiTableCell-body {
    border: none;
  }
`;

const StyledBodyRow = styled(TableRow)`
  background-color: #fff;
`;

const StyledHeadCell = styled(TableCell)`
  background-color: rgba(0, 72, 120, 0.1);
  color: ${cuideoTheme.palette.primary.light};
  font-size: 0.75rem;
  line-height: normal;
  font-weight: 700;
`;

const StyledBodyCell = styled(TableCell)`
  font-size: 0.875rem;
  color: ${cuideoTheme.palette.primary.main};
  line-height: 1.25;
`;

const StyledReasonCell = styled(StyledBodyCell)`
  white-space: pre-line;
`;

const StyledLoadingWrapper = styled(TableCell)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 4rem;
  width: 100%;
`;

const StyledErrorWrapper = styled(TableCell)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 4rem;
`;

const StyledEmptyText = styled(Typography)`
  font-size: 0.875rem;
  text-align: center;
`;

const COLUMN_COUNT = 3;

interface IClientDiscardedValuationsTableProps {
  candidateId: string;
}

const ClientDiscardedValuationsTable = (
  props: IClientDiscardedValuationsTableProps
) => {
  const { candidateId } = props;

  const valuationsQuery = useClientDiscardedValuations({
    params: { candidateId },
  });

  const status = valuationsQuery.status;

  return (
    <StyledTable>
      <TableHead>
        <TableRow>
          <StyledHeadCell>
            <FormattedMessage
              id="ClientDiscardedValuationsTable.Contract"
              defaultMessage="Contrato"
            />
          </StyledHeadCell>
          <StyledHeadCell>
            <FormattedMessage
              id="ClientDiscardedValuationsTable.Date"
              defaultMessage="Fecha"
            />
          </StyledHeadCell>
          <StyledHeadCell>
            <FormattedMessage
              id="ClientDiscardedValuationsTable.DiscardReason"
              defaultMessage="Motivo del descarte"
            />
          </StyledHeadCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {status === "loading" && (
          <StyledBodyRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <StyledLoadingWrapper>
                <CircularProgress thickness={5} size={22} />
              </StyledLoadingWrapper>
            </TableCell>
          </StyledBodyRow>
        )}
        {status === "error" && (
          <StyledBodyRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <StyledErrorWrapper>
                <Alert severity="error">
                  <FormattedMessage
                    id="ClientDiscardedValuationsTable.Ha habido un error"
                    defaultMessage="Ha habido un error"
                  />
                </Alert>
              </StyledErrorWrapper>
            </TableCell>
          </StyledBodyRow>
        )}
        {status === "success" && valuationsQuery.data && (
          <>
            {valuationsQuery.data["hydra:member"].length === 0 ? (
              <StyledBodyRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <StyledEmptyText>
                    <FormattedMessage
                      id="ClientDiscardedValuationsTable.Ningún cliente ha descartado a este candidato/a"
                      defaultMessage="Ningún cliente ha descartado a este candidato/a"
                    />
                  </StyledEmptyText>
                </TableCell>
              </StyledBodyRow>
            ) : (
              valuationsQuery.data["hydra:member"].map((valuation) => (
                <StyledBodyRow key={valuation.id}>
                  <StyledBodyCell>{valuation.contratoName}</StyledBodyCell>
                  <StyledBodyCell>
                    {moment(valuation.statusUpdatedAt).format(
                      "DD/MM/YYYY HH:mm"
                    )}
                  </StyledBodyCell>
                  <StyledReasonCell>
                    {valuation.discard_reason}
                  </StyledReasonCell>
                </StyledBodyRow>
              ))
            )}
          </>
        )}
      </TableBody>
    </StyledTable>
  );
};

export default ClientDiscardedValuationsTable;
